
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface Notification {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface NotificationContextType {
  showNotification: (message: string, type?: 'success' | 'error' | 'info') => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const dismiss = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const showNotification = useCallback((message: string, type: 'success' | 'error' | 'info' = 'success') => {
    const id = Math.random().toString(36).substring(2, 9);
    setNotifications((prev) => [...prev, { id, message, type }]);
    
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 4000);
  }, []);

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      <div className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[100] flex flex-col items-end pointer-events-none space-y-2 max-w-[92vw] sm:max-w-md">
        <AnimatePresence>
          {notifications.map((n) => {
            const isSuccess = n.type === 'success';
            const isError = n.type === 'error';

            return (
              <motion.div
                key={n.id}
                layout
                initial={{ opacity: 0, x: 35, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 25, scale: 0.95, transition: { duration: 0.15 } }}
                className="pointer-events-auto w-full sm:w-auto"
              >
                <div className="relative group overflow-hidden bg-slate-900 border border-purple-900/80 hover:border-purple-600/70 shadow-xl shadow-black/50 rounded-xl py-2.5 px-3.5 flex items-center space-x-3 transition-colors">

                  {/* Conteúdo da Mensagem */}
                  <div className="flex-1 min-w-0 pr-1">
                    <p className="text-xs sm:text-[13px] font-medium text-slate-200 leading-snug break-words">
                      {n.message}
                    </p>
                  </div>

                  {/* Botão de Fechar */}
                  <button
                    type="button"
                    onClick={() => dismiss(n.id)}
                    className="shrink-0 p-1 text-slate-400 hover:text-white rounded-md transition-colors cursor-pointer hover:bg-slate-800 active:scale-95"
                    title="Fechar notificação"
                  >
                    <i className="fas fa-xmark text-xs"></i>
                  </button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </NotificationContext.Provider>
  );
};

